import React, { useCallback, useEffect, useState } from 'react';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  LoadingOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { servicesService } from '../../services';
import { useNotificationContext } from '../../contexts/NotificationContext';

/**
 * Servicios permitidos (regla SG-a-SG): permite que un app-deploy o una VM se
 * conecten a una base de datos (RDS o SQL Server Express de marketplace).
 * Usa los endpoints genéricos POST/DELETE /services/{id}/allow-service
 * (lambda security_group_permission).
 */

interface AllowedServicesTabProps {
  /** id del servicio destino (la BD que recibe la conexión). */
  serviceId: string;
}

interface AllowedEntry {
  serviceId: string;
  name?: string;
}

const parseAllowed = (svc: any): AllowedEntry[] => {
  const src = svc?.securityRulesService;
  if (!Array.isArray(src)) return [];
  return src
    .map((s: any): AllowedEntry | null => {
      if (typeof s === 'string') return s.trim() ? { serviceId: s.trim() } : null;
      const serviceId = s?.service_id || s?.serviceId || s?.source || s?.id;
      if (!serviceId) return null;
      return { serviceId, name: s?.name || s?.serviceName };
    })
    .filter(Boolean) as AllowedEntry[];
};

const AllowedServicesTab: React.FC<AllowedServicesTabProps> = ({ serviceId }) => {
  const { success, error: showError } = useNotificationContext();
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState<AllowedEntry[]>([]);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [selected, setSelected] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const svc: any = await servicesService.getById(serviceId);
      setAllowed(parseAllowed(svc));
      const [dep, vm] = await Promise.all([
        servicesService.getByCategory('APP_DEPLOY', { limit: 200 }),
        servicesService.getByCategory('VM', { limit: 200 }),
      ]);
      const merged = [...(dep.services || []), ...(vm.services || [])];
      const uniq = Array.from(new Map(merged.map((s: any) => [s.id, s])).values()).filter(
        (s: any) => s.id && s.id !== serviceId && s.securityGroupId
      );
      setCandidates(uniq);
    } catch (e) {
      console.error('Error cargando servicios permitidos:', e);
      showError('No se pudieron cargar los servicios');
    } finally {
      setLoading(false);
    }
  }, [serviceId, showError]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!selected) return;
    if (allowed.some((a) => a.serviceId === selected)) {
      showError('Ese servicio ya está permitido');
      return;
    }
    setAdding(true);
    try {
      await servicesService.allowDbServiceSource(serviceId, { source: selected });
      success('Servicio permitido');
      setSelected('');
      await load();
    } catch (e: any) {
      showError(e?.message || 'No se pudo permitir el servicio');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (sid: string) => {
    setRemovingId(sid);
    try {
      await servicesService.revokeDbServiceSource(serviceId, { source: sid });
      success('Servicio removido');
      await load();
    } catch (e: any) {
      showError(e?.message || 'No se pudo remover el servicio');
    } finally {
      setRemovingId(null);
    }
  };

  const nameFor = (sid: string) =>
    candidates.find((c) => c.id === sid)?.name ||
    allowed.find((a) => a.serviceId === sid)?.name ||
    sid;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-8 justify-center">
        <LoadingOutlined /> Cargando…
      </div>
    );
  }

  const selectable = candidates.filter((c) => !allowed.some((a) => a.serviceId === c.id));

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <ApiOutlined /> Servicios permitidos
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Permite que un app-deploy o una VM se conecten a esta base de datos (regla de red entre sus
          grupos de seguridad, sin abrirla a internet).
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-black focus:border-transparent"
        >
          <option value="">Selecciona un servicio…</option>
          {selectable.map((c: any) => (
            <option key={c.id} value={c.id}>
              {c.name || c.id} · {(c.category || c.type || '').toString()}
            </option>
          ))}
        </select>
        <button
          onClick={handleAdd}
          disabled={!selected || adding}
          className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
        >
          <PlusOutlined /> {adding ? 'Permitiendo…' : 'Permitir'}
        </button>
        <button
          onClick={load}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          title="Refrescar"
        >
          <ReloadOutlined />
        </button>
      </div>

      {allowed.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
          Ningún servicio tiene acceso todavía.
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {allowed.map((a) => (
              <li
                key={a.serviceId}
                className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-900"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {a.name || nameFor(a.serviceId)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{a.serviceId}</p>
                </div>
                <button
                  onClick={() => handleRemove(a.serviceId)}
                  disabled={removingId === a.serviceId}
                  className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50"
                  title="Remover"
                >
                  {removingId === a.serviceId ? <LoadingOutlined /> : <DeleteOutlined />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AllowedServicesTab;
